(function () {
  'use strict';

  if (customElements.get('modal-dialog')) return;

  var focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  class ModalDialog extends HTMLElement {
    constructor() {
      super();
      this.modal = this.querySelector('.modal');
      this.modalContent = this.querySelector('.modal-content');
      this.closeButton = this.querySelector('.modal-close');
      this.form = this.querySelector('form');
      this.opener = null;
      this.handleKeydown = this.handleKeydown.bind(this);
    }

    connectedCallback() {
      if (!this.hasAttribute('role')) this.setAttribute('role', 'dialog');
      if (!this.hasAttribute('aria-modal')) this.setAttribute('aria-modal', 'true');
      if (!this.hasAttribute('aria-hidden')) this.setAttribute('aria-hidden', 'true');
      if (this.modalContent && !this.modalContent.hasAttribute('tabindex')) {
        this.modalContent.setAttribute('tabindex', '-1');
      }
      this.modalTriggers = document.querySelectorAll('[data-modal-trigger="' + this.id + '"], a[href*="#' + this.id + '"]');
      this.bindEvents();
    }

    bindEvents() {
      this.modalTriggers.forEach((trigger) => {
        trigger.setAttribute('aria-controls', this.id);
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-expanded', 'false');
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          this.opener = trigger;
          this.open();
        });
      });

      if (this.closeButton) {
        this.closeButton.addEventListener('click', () => this.close());
      }

      if (this.modal) {
        this.modal.addEventListener('click', (event) => {
          if (event.target === this.modal) this.close();
        });
      }

      if (this.form) {
        this.form.addEventListener('submit', () => {
          this.dispatchEvent(new CustomEvent('modal:formSubmit', {
            bubbles: true,
            detail: { form: this.form }
          }));
        });
      }

      document.addEventListener('keydown', this.handleKeydown);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.handleKeydown);
    }

    handleKeydown(event) {
      if (!this.isOpen()) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;

      var focusable = Array.prototype.filter.call(
        this.querySelectorAll(focusableSelector),
        function (element) {
          return !element.hidden && element.getAttribute('aria-hidden') !== 'true' && element.getClientRects().length > 0;
        }
      );

      if (!focusable.length) {
        event.preventDefault();
        this.modalContent.focus();
        return;
      }

      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    open() {
      if (!this.modal || this.isOpen()) return;

      this.setAttribute('aria-hidden', 'false');
      this.modal.classList.add('open');
      this.modalTriggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'true'));

      // not focusable until the open transition has applied
      var initialFocus = this.querySelector('[data-modal-initial-focus]') || this.modalContent;
      var attempts = 0;
      var takeFocus = function () {
        initialFocus.focus();
        if (document.activeElement !== initialFocus && ++attempts < 20) {
          window.requestAnimationFrame(takeFocus);
        }
      };
      window.requestAnimationFrame(takeFocus);

      var cartLinkField = this.querySelector('#sharecartCartLink');
      if (cartLinkField) {
        this.generateSharedCartUrl().then(function (url) {
          cartLinkField.value = url;
        });
      }

      this.dispatchEvent(new CustomEvent('modal:opened', { bubbles: true }));
    }

    close() {
      if (!this.modal || !this.isOpen()) return;

      this.modal.classList.remove('open');
      this.setAttribute('aria-hidden', 'true');
      this.modalTriggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));

      var opener = this.opener;
      window.requestAnimationFrame(function () {
        if (opener && document.contains(opener)) opener.focus();
      });

      this.dispatchEvent(new CustomEvent('modal:closed', { bubbles: true }));
    }

    isOpen() {
      return this.getAttribute('aria-hidden') === 'false' && this.modal && this.modal.classList.contains('open');
    }

    setFormData(data) {
      Object.keys(data).forEach((key) => {
        var input = this.querySelector('[name="' + key + '"]');
        if (input) input.value = data[key];
      });
    }

    resetForm() {
      if (this.form) this.form.reset();
    }

    async generateSharedCartUrl() {
      try {
        var response = await fetch('/cart.js');
        var cart = await response.json();
        var items = cart.items || [];

        if (items.length === 0) {
          return window.location.origin + '/?shared-cart=true&items=';
        }

        var itemsParam = items.map(function (item) {
          return item.variant_id + ':' + item.quantity;
        }).join(',');

        return window.location.origin + '/cart/?shared-cart=true&items=' + itemsParam;
      } catch (error) {
        console.error('Error generating shared cart URL:', error);
        return window.location.origin + '/cart/?shared-cart=true&items=';
      }
    }
  }

  customElements.define('modal-dialog', ModalDialog);
})();
